import { SearchOff } from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";
import {
  Alert,
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslationHook } from "../../hooks";
import { ExportDefinition } from "../../types/model/export";
import { ExportRow } from "../../types/model/export/ExportRow";
import RunExportDialog from "./ExportDialog";
import UpdateExportDefinitionDialog from "./UpdateExportDefinitionDialog";

interface ExportsTableProps {
  tableData: ExportRow[];
  onRequestRefresh: () => void;
  disablePagination?: boolean;
  mode?: "run" | "admin";
}

const ExportsTable = ({
  tableData,
  onRequestRefresh,
  disablePagination = false,
  mode = "run",
}: ExportsTableProps) => {
  const { t } = useTranslationHook();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ExportRow | null>(null);

  const scopeLabels: Record<string, string> = {
    capture_id: t("exports.table.capture"),
    flow_test_id: t("exports.table.flow-test"),
  };

  const handleClick = (row: ExportRow) => {
    setSelectedRow(row);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedRow(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = tableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tableData.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h4">
            {t("exports.table.title")}
          </Typography>

          <Tooltip title={t("generic.refresh")}>
            <IconButton onClick={onRequestRefresh}>
              <CachedIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>

        {mode === "run" ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("exports.table.description")}
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("exports.admin.description")}
          </Alert>
        )}

        <TableContainer>
          <Table sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("exports.table.name")}</TableCell>
                <TableCell>{t("exports.table.scope")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 10 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        opacity: 0.6,
                      }}
                    >
                      <SearchOff sx={{ fontSize: 64, mb: 2 }} />
                      <Typography variant="h6">
                        {t("exports.table.no-exports")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {visibleRows.map((row) => (
                <TableRow
                  hover
                  key={row.id}
                  onClick={() => handleClick(row)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    {row.scopeColumn
                      ? (scopeLabels[row.scopeColumn] ?? row.scopeColumn)
                      : "-"}
                  </TableCell>
                </TableRow>
              ))}

              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={3} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!disablePagination && (
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={tableData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {mode === "run" ? (
        <RunExportDialog
          open={dialogOpen}
          exportRow={selectedRow}
          onClose={handleDialogClose}
          onSuccess={onRequestRefresh}
        />
      ) : (
        <UpdateExportDefinitionDialog
          open={dialogOpen}
          exportDefinition={selectedRow as unknown as ExportDefinition}
          onClose={handleDialogClose}
          onSuccess={onRequestRefresh}
        />
      )}
    </Box>
  );
};

export default ExportsTable;